import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost } from "../api";
import type { Datified, Schemas } from "../../api/types";
import { POLL_BASE_MS } from "./intervals";

export type APIKey = Datified<Schemas["APIKey"], "created_at" | "last_used_at">;
export type APIKeyCreate = Schemas["APIKeyCreate"];
export type APIKeyCreated = APIKey & { token: string };

/** `includeDeleted` is the page's "show deleted" switch; see useWorkerTokenList. */
export function useAPIKeyList(includeDeleted = false) {
  return useQuery<APIKey[]>({
    queryKey: ["api-keys", { includeDeleted }],
    queryFn: async () => {
      const res = await apiGet<{ items: APIKey[] }>(
        includeDeleted ? "/api-keys?include_deleted=true" : "/api-keys",
      );
      return res.items;
    },
    refetchInterval: POLL_BASE_MS,
  });
}

export function useCreateAPIKey() {
  const qc = useQueryClient();
  return useMutation<APIKeyCreated, unknown, APIKeyCreate>({
    meta: { suppressErrorToast: true },
    mutationFn: (input) => apiPost<APIKeyCreated>("/api-keys", input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["api-keys"] }),
  });
}

export function useRevokeAPIKey() {
  const qc = useQueryClient();
  return useMutation<void, unknown, { id: string }>({
    meta: { suppressErrorToast: true },
    mutationFn: ({ id }) => apiPost<void>(`/api-keys/${id}/revoke`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["api-keys"] }),
  });
}

/** Hides a revoked key. Refused by the server while the key is still active. */
export function useDeleteAPIKey() {
  const qc = useQueryClient();
  return useMutation<void, unknown, { id: string }>({
    meta: { suppressErrorToast: true },
    mutationFn: ({ id }) => apiDelete<void>(`/api-keys/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["api-keys"] }),
  });
}
